<template>
  <div>
    <div class="page-header page-header-small">
      <parallax
        class="page-header-image"
        style="background-image: url('/img/tech.jpg')"
      >
      </parallax>
      <div class="content-center">
        <div class="container">
          <h1 class="title">Technical Projects</h1>
        </div>
      </div>
    </div>

    <div class="section tech-detail-section">
      <div class="container tech-detail-container">
        <div class="tech-detail-topbar">
          <router-link :to="{ name: 'tech' }" class="tech-back-link">
            <i class="now-ui-icons arrows-1_minimal-left"></i>
            기술 포트폴리오로 돌아가기
          </router-link>
        </div>
        <div class="row tech-detail-layout">
          <div class="col-lg-2">
            <aside class="tech-detail-sidebar">
              <div class="tech-info-card">
                <h4>Project Info</h4>
                <dl class="tech-meta-list">
                  <div>
                    <dt>기간</dt>
                    <dd>{{ project.period }}</dd>
                  </div>
                  <div>
                    <dt>역할</dt>
                    <dd>{{ project.role }}</dd>
                  </div>
                  <div>
                    <dt>분류</dt>
                    <dd>{{ project.category }}</dd>
                  </div>
                  <div>
                    <dt>요약</dt>
                    <dd>{{ project.summary }}</dd>
                  </div>
                </dl>
              </div>

              <div class="tech-info-card">
                <h4>Stack</h4>
                <div class="tech-stack-list">
                  <span
                    v-for="stack in project.stack"
                    :key="`${project.slug}-${stack}`"
                    class="tech-stack-chip"
                  >
                    {{ stack }}
                  </span>
                </div>
              </div>
            </aside>
          </div>

          <div class="col-lg-8">
            <article class="tech-markdown-card">
              <header class="tech-markdown-header">
                <p class="tech-markdown-category">{{ project.category }}</p>
                <h2 class="tech-markdown-title">{{ project.title }}</h2>
              </header>
              <div class="tech-markdown-divider"></div>
              <div v-if="isLoading" class="tech-markdown-placeholder">
                본문을 불러오는 중입니다.
              </div>
              <div v-else-if="loadError" class="tech-markdown-placeholder">
                {{ loadError }}
              </div>
              <div
                v-else
                class="tech-markdown-body"
                v-html="renderedMarkdown"
              ></div>
            </article>
          </div>

          <div class="col-lg-2">
            <aside
              v-if="markdownBookmarks.length"
              class="tech-detail-sidebar tech-bookmark-sidebar"
            >
              <div class="tech-bookmark-card">
                <h4>Bookmarks</h4>
                <nav class="tech-bookmark-nav">
                  <a
                    v-for="bookmark in markdownBookmarks"
                    :key="bookmark.id"
                    :href="`#${bookmark.id}`"
                    class="tech-bookmark-link"
                    :class="`level-${bookmark.level}`"
                  >
                    {{ bookmark.text }}
                  </a>
                </nav>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Parallax } from '@/components';
import projects from './data';
import { extractMarkdownHeadings, renderMarkdown } from './markdown';

export default {
  name: 'tech-detail',
  bodyClass: 'landing-page',
  components: {
    Parallax
  },
  data() {
    return {
      markdownSource: '',
      isLoading: false,
      loadError: ''
    };
  },
  computed: {
    project() {
      const project = projects.find(
        item => item.slug === this.$route.params.slug
      );

      return (
        project || {
          slug: '',
          title: '프로젝트를 찾을 수 없습니다',
          category: 'Unknown',
          period: '-',
          role: '-',
          summary:
            '요청한 프로젝트 정보가 존재하지 않습니다. 목록 페이지에서 다시 선택해 주세요.',
          coverImage: 'img/tech.jpg',
          stack: [],
          markdownPath: ''
        }
      );
    },
    renderedMarkdown() {
      return renderMarkdown(this.markdownSource);
    },
    markdownBookmarks() {
      return extractMarkdownHeadings(this.markdownSource);
    }
  },
  created() {
    this.loadMarkdown();
  },
  watch: {
    '$route.params.slug'() {
      this.loadMarkdown();
    }
  },
  methods: {
    async loadMarkdown() {
      if (!this.project.markdownPath) {
        this.markdownSource =
          '# 프로젝트를 찾을 수 없습니다\n\n목록 페이지로 돌아가 다른 프로젝트를 선택해 주세요.';
        this.loadError = '';
        this.isLoading = false;
        return;
      }

      this.isLoading = true;
      this.loadError = '';

      try {
        const response = await fetch(this.project.markdownPath);

        if (!response.ok) {
          throw new Error('failed');
        }

        this.markdownSource = await response.text();
      } catch (error) {
        this.markdownSource = '';
        this.loadError = '본문 Markdown 파일을 불러오지 못했습니다.';
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.tech-back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #4c6b8a;
  font-weight: 600;
}

.tech-back-link:hover {
  color: #1683d8;
  text-decoration: none;
}

.tech-detail-section {
  padding-bottom: 80px;
}

.tech-detail-container {
  max-width: 1680px;
}

.tech-detail-topbar {
  margin-bottom: 24px;
}

.tech-detail-layout {
  justify-content: center;
}

.tech-detail-sidebar {
  position: sticky;
  top: 110px;
}

.tech-info-card,
.tech-markdown-card,
.tech-bookmark-card {
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 20px 45px rgba(44, 62, 80, 0.12);
}

.tech-info-card {
  padding: 24px;
}

.tech-info-card + .tech-info-card {
  margin-top: 20px;
}

.tech-info-card h4 {
  margin-top: 0;
  margin-bottom: 20px;
}

.tech-meta-list {
  margin-bottom: 0;
}

.tech-meta-list div + div {
  margin-top: 18px;
}

.tech-meta-list dt {
  margin-bottom: 6px;
  color: #6c757d;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.tech-meta-list dd {
  margin-bottom: 0;
  color: #2c3e50;
  font-weight: 600;
  line-height: 1.6;
}

.tech-stack-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tech-stack-chip {
  padding: 7px 12px;
  border-radius: 999px;
  background: rgba(44, 168, 255, 0.1);
  color: #1683d8;
  font-size: 0.8rem;
  font-weight: 700;
}

.tech-markdown-card {
  padding: 34px 34px 28px;
}

.tech-markdown-header {
  margin-bottom: 28px;
}

.tech-markdown-category {
  margin-bottom: 10px;
  color: #1683d8;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.tech-markdown-title {
  margin: 0;
  color: #2c3e50;
}

.tech-markdown-divider {
  height: 1px;
  margin-bottom: 28px;
  background: rgba(44, 62, 80, 0.12);
}

.tech-bookmark-card {
  padding: 24px;
}

.tech-bookmark-card h4 {
  margin-top: 0;
  margin-bottom: 18px;
}

.tech-bookmark-nav {
  display: flex;
  max-height: calc(100vh - 230px);
  overflow-y: auto;
  padding-right: 6px;
  flex-direction: column;
  gap: 10px;
}

.tech-bookmark-nav::-webkit-scrollbar {
  width: 6px;
}

.tech-bookmark-nav::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(44, 168, 255, 0.32);
}

.tech-bookmark-nav::-webkit-scrollbar-track {
  background: rgba(44, 62, 80, 0.06);
}

.tech-bookmark-link {
  color: #5c7085;
  font-size: 0.95rem;
  line-height: 1.5;
  text-decoration: none;
}

.tech-bookmark-link:hover {
  color: #1683d8;
  text-decoration: none;
}

.tech-bookmark-link.level-2 {
  padding-left: 12px;
  font-size: 0.9rem;
}

.tech-bookmark-link.level-3 {
  padding-left: 24px;
  font-size: 0.86rem;
}

.tech-markdown-body {
  color: #3f4d5a;
  line-height: 1.9;
  word-break: keep-all;
  font-weight: 500;
}

.tech-markdown-placeholder {
  color: #6c757d;
  line-height: 1.8;
}

.tech-markdown-body :deep(h1),
.tech-markdown-body :deep(h2),
.tech-markdown-body :deep(h3) {
  scroll-margin-top: 104px;
  margin-top: 34px;
  margin-bottom: 14px;
  color: #2c3e50;
}

.tech-markdown-body :deep(h1) {
  font-size: 1.9rem;
}

.tech-markdown-body :deep(h2) {
  font-size: 1.45rem;
}

.tech-markdown-body :deep(h3) {
  font-size: 1.15rem;
}

.tech-markdown-body :deep(h1:first-child),
.tech-markdown-body :deep(h2:first-child),
.tech-markdown-body :deep(h3:first-child) {
  margin-top: 0;
}

.tech-markdown-body :deep(p) {
  margin-bottom: 16px;
  font-weight: 500;
}

.tech-markdown-body :deep(ul) {
  padding-left: 1.2rem;
  margin-bottom: 18px;
}

.tech-markdown-body :deep(li) {
  font-size: 1rem;
  line-height: 1.9;
}

.tech-markdown-body :deep(li + li) {
  margin-top: 8px;
}

.tech-markdown-body :deep(blockquote) {
  margin: 24px 0;
  padding: 14px 18px;
  border-left: 4px solid #2ca8ff;
  border-radius: 0 12px 12px 0;
  background: rgba(44, 168, 255, 0.08);
  color: #3d5a73;
}

.tech-markdown-body :deep(.md-table-wrap) {
  margin: 24px 0;
  overflow-x: auto;
  border: 1px solid rgba(44, 62, 80, 0.1);
  border-radius: 12px;
}

.tech-markdown-body :deep(.md-table) {
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
  background: #fff;
}

.tech-markdown-body :deep(.md-table th),
.tech-markdown-body :deep(.md-table td) {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(44, 62, 80, 0.08);
  text-align: left;
  vertical-align: top;
}

.tech-markdown-body :deep(.md-table th) {
  background: rgba(44, 168, 255, 0.08);
  color: #2c3e50;
  font-weight: 800;
}

.tech-markdown-body :deep(.md-table tr:last-child td) {
  border-bottom: 0;
}

.tech-markdown-body :deep(.md-table th.md-table-align-left),
.tech-markdown-body :deep(.md-table td.md-table-align-left) {
  text-align: left;
}

.tech-markdown-body :deep(.md-table th.md-table-align-center),
.tech-markdown-body :deep(.md-table td.md-table-align-center) {
  text-align: center;
}

.tech-markdown-body :deep(.md-table th.md-table-align-right),
.tech-markdown-body :deep(.md-table td.md-table-align-right) {
  text-align: right;
}

.tech-markdown-body :deep(.md-table th.md-table-valign-top),
.tech-markdown-body :deep(.md-table td.md-table-valign-top) {
  vertical-align: top;
}

.tech-markdown-body :deep(.md-table th.md-table-valign-middle),
.tech-markdown-body :deep(.md-table td.md-table-valign-middle) {
  vertical-align: middle;
}

.tech-markdown-body :deep(.md-table th.md-table-valign-bottom),
.tech-markdown-body :deep(.md-table td.md-table-valign-bottom) {
  vertical-align: bottom;
}

.tech-markdown-body :deep(.md-youtube-embed) {
  position: relative;
  margin: 26px 0;
  overflow: hidden;
  border-radius: 14px;
  background: #111;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.12);
  aspect-ratio: 16 / 9;
}

.tech-markdown-body :deep(.md-youtube-embed iframe) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.tech-markdown-body :deep(a) {
  color: #1683d8;
  font-weight: 600;
}

.tech-markdown-body :deep(.md-inline-code) {
  padding: 0.18rem 0.42rem;
  border-radius: 6px;
  background: #f1f3f5;
  color: #d6336c;
  font-size: 0.9em;
}

.tech-markdown-body :deep(.md-code-block) {
  margin: 24px 0;
  padding: 18px 20px;
  overflow-x: auto;
  border-radius: 14px;
  background: #1f2937;
  color: #f8f9fa;
}

@media (max-width: 991px) {
  .tech-detail-sidebar {
    position: static;
    margin-bottom: 24px;
  }

  .tech-bookmark-sidebar {
    margin-top: 24px;
  }

  .tech-bookmark-nav {
    max-height: 280px;
  }
}

@media (max-width: 767px) {
  .tech-markdown-card {
    padding: 26px 22px 20px;
  }
}
</style>
